const STORAGE_KEY = "myspeechplan.jwt";

/**
 * The JWT lives in localStorage so a page reload does not force a new login.
 *
 * The trade-off is deliberate: localStorage is readable by any script running
 * on this origin, so a cross-site scripting hole would expose the token. The
 * alternatives are worse here — there is no refresh-token flow, and keeping the
 * token in memory only would log the user out on every reload. What limits the
 * damage is that the token is short lived (20 minutes, renewed while the
 * session is in use) and the server enforces an independent 14 minute idle
 * timeout, after which it rotates the jti and every token of that session is
 * dead even if someone copied one.
 *
 * Do not move the token into a cookie without revisiting CSRF: the Rails app
 * has no session and therefore no forgery protection, which is only safe as
 * long as the credential travels in a header that a foreign origin cannot make
 * the browser send.
 */
export const tokenStore = {
  get(): string | null {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch {
      // Private mode / storage disabled — treat as "not logged in".
      return null;
    }
  },

  set(token: string): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, token);
    } catch {
      /* ignore */
    }
  },

  clear(): void {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  },
};

/**
 * The `exp` claim of a token, in milliseconds, or null if it cannot be read.
 *
 * The payload is only *read*, never trusted: the signature is the server's
 * business and this side has no secret to check it with. What the expiry is
 * good for is recognising a token that is demonstrably useless — one whose life
 * ended hours ago — before it is sent or, worse, stored.
 */
export function tokenExpiresAt(token: string | null): number | null {
  if (!token) return null;

  const jwt = token.replace(/^Bearer\s+/i, "");
  const payload = jwt.split(".")[1];
  if (!payload) return null;

  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const claims = JSON.parse(window.atob(padded)) as { exp?: number };
    return typeof claims.exp === "number" ? claims.exp * 1000 : null;
  } catch {
    // Not a JWT, or not one this build understands — let the server decide.
    return null;
  }
}

/** True only when the token says so itself; an unreadable one is not "expired". */
export function isTokenExpired(token: string | null): boolean {
  const expiresAt = tokenExpiresAt(token);
  return expiresAt !== null && expiresAt <= Date.now();
}
